import * as Sentry from "@sentry/react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import PageSpinner from "components/Spinners/PageSpinner";
import AuthLayout from "layouts/Auth";
import AuthBootstrap from "layouts/AuthBootstrap";
import { setupQueryBroadcast } from "lib/queryBroadcast";
import { persistOptions } from "lib/queryCachePersist";
import { queryClient } from "lib/queryClient";
import React from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { useProfile } from "./hooks/useProfile";
import { AuthProvider } from "./reducers/auth";
import { RefererProvider, useSetReferer } from "./reducers/referer";
import { ViewerModeProvider } from "./reducers/viewerMode";

// The admin shell (navbar/sidebar + all admin-only ecosystem) is lazy so the
// auth/login entry bundle stays minimal; it loads after sign-in.
const AdminLayout = React.lazy(() => import("layouts/admin/Admin"));

const App = (): React.ReactElement => {
  const setReferer = useSetReferer();
  const location = useLocation();

  React.useEffect(() => {
    setReferer(location);
  }, [location, setReferer]);

  const userProfile = useProfile();
  return (
    <React.Suspense fallback={<PageSpinner spin>{null}</PageSpinner>}>
      <SentryRoutes>
        {userProfile && <Route path="/admin/*" element={<AdminLayout />} />}
        <Route path="/auth/*" element={<AuthLayout />} />
        <Route path={"*"} element={<Navigate replace to={"/auth/login"} />} />
      </SentryRoutes>
    </React.Suspense>
  );
};

export const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const RootApp = (element?: HTMLElement): void => {
  setupQueryBroadcast();
  const root = createRoot(
    element || (document.getElementById("root") as HTMLElement),
  );
  root.render(
    <Sentry.ErrorBoundary
      fallback={
        window.runConfig?.environment === "production" ? (
          <p>An error has occurred</p>
        ) : (
          ({ error, componentStack, resetError }) => (
            <React.Fragment>
              <div>You have encountered an error</div>
              <div>{JSON.stringify(error)}</div>
              <div>{componentStack}</div>
              <button
                type={"button"}
                onClick={() => {
                  resetError();
                }}
              >
                Click here to reset!
              </button>
            </React.Fragment>
          )
        )
      }
    >
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={persistOptions}
      >
        <ReactQueryDevtools initialIsOpen={false} />
        <BrowserRouter>
          <RefererProvider>
            <AuthProvider>
              <AuthBootstrap>
                <ViewerModeProvider>
                  <App />
                </ViewerModeProvider>
              </AuthBootstrap>
            </AuthProvider>
          </RefererProvider>
        </BrowserRouter>
      </PersistQueryClientProvider>
    </Sentry.ErrorBoundary>,
  );
};

export default RootApp;
